import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { formatNumber, formatPercentage, formatPrice } from "utility/formatters";

import ResponsiveDataGrid from "../shared/DataGrid/ResponsiveDataGrid";

export function ProductionSummaryTable({ currentCountyPractice, practiceCalculations, _getUnitOfMeasureAbbreviation, cropProfileBasis = 0 }) {
  const _cropProfileBasis = Number(cropProfileBasis) || 0;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const commodityName = currentCountyPractice?.commodity_name;
  const unitAbbreviation = practiceCalculations[commodityName]?.unitAbbreviation;

  const columns = [
    {
      field: "acres",
      headerName: "Acres",
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <Box component="span" sx={{ display: "block" }}>
          {value?.map((item, index) => (
            <Box key={`${item.name}-${index}`} component="span" sx={{ display: "block" }}>
              {item.name}: {formatNumber(item.acres, 0)}
            </Box>
          ))}
        </Box>
      ),
    },
    {
      field: "yield",
      headerName: "Yield (avg)",
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <Box component="span" sx={{ display: "block" }}>
          {value?.map((item, index) => (
            <Box key={`${item.name}-${index}`} component="span" sx={{ display: "block" }}>
              {item.name}: {formatNumber(item.yield, 0)} {unitAbbreviation}
            </Box>
          ))}
        </Box>
      ),
    },
    {
      field: "production",
      headerName: "Production",
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <Box component="span" sx={{ display: "block" }}>
          {formatNumber(value.production, 0)} {_getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase()}
        </Box>
      ),
    },
    {
      field: "insuredGuarantee",
      headerName: "Insured Guarantee",
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <Box component="span" sx={{ display: "block" }}>
          {formatNumber(value.production_guarantee)}{" "}
          {_getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase()}
        </Box>
      ),
    },
    {
      field: "marketingGoal",
      headerName: "Marketing Goal",
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <Box component="span" sx={{ display: "block" }}>
          {formatNumber(value.marketing_goal_units)}{" "}
          {_getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase()}
        </Box>
      ),
    },
    {
      field: "hedged",
      headerName: "Hedged",
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <Box component="span" sx={{ display: "block" }}>
          {formatNumber(value.unit_quantity_hedge_total)}{" "}
          {_getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase()}
        </Box>
      ),
    },
    {
      field: "board",
      headerName: "Board",
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <Box component="span" sx={{ display: "block" }}>
          {value.market_symbol}
          <br />
          {formatPrice(value.market_last_price, 4)}
          {_cropProfileBasis !== 0 && (
            <>
              <br />
              {`Basis: ${formatPrice(_cropProfileBasis, 4)}`}
            </>
          )}
        </Box>
      ),
    },
  ];

  const rows = [
    {
      id: 1,
      acres: practiceCalculations[commodityName]?.acresDisplay,
      yield: practiceCalculations[commodityName]?.yieldDisplay,
      production: {
        production: currentCountyPractice?.production,
        amount: currentCountyPractice?.amount,
      },
      insuredGuarantee: {
        production_guarantee: currentCountyPractice?.production_guarantee,
        sales_amount: currentCountyPractice?.sales_amount,
      },
      marketingGoal: {
        marketing_goal_units: currentCountyPractice?.marketing_goal_units,
      },
      hedged: {
        unit_quantity_hedge_total: currentCountyPractice?.unit_quantity_hedge_total,
        production: currentCountyPractice?.production,
      },
      board: {
        market_symbol: currentCountyPractice?.market_symbol,
        market_last_price: currentCountyPractice?.market_last_price,
      },
    },
  ];

  if (isMobile) {
    return (
      <ResponsiveDataGrid
        rows={rows}
        columns={columns}
        hideFooter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
      />
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 440 }}>
      <Table stickyHeader aria-label="sticky table" className="reportTable marketing-table">
        <TableHead>
          <TableRow>
            <TableCell>Acres</TableCell>
            <TableCell>Yield (avg)</TableCell>
            <TableCell>Production</TableCell>
            <TableCell>Insured Guarantee</TableCell>
            <TableCell>Marketing Goal</TableCell>
            <TableCell>Hedged</TableCell>
            <TableCell>Board</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow hover role="checkbox" tabIndex={-1}>
            <TableCell>
              {practiceCalculations[currentCountyPractice.commodity_name]?.acresDisplay?.map((item, index) => (
                <div key={`${item.name}-${index}`}>
                  {item.name}: {formatNumber(item.acres, 0)}
                </div>
              ))}
            </TableCell>
            <TableCell>
              {practiceCalculations[currentCountyPractice.commodity_name]?.yieldDisplay?.map((item, index) => (
                <div key={`${item.name}-${index}`}>
                  {item.name}: {formatNumber(item.yield, 0)}
                  {" " + practiceCalculations[currentCountyPractice.commodity_name]?.unitAbbreviation}
                </div>
              ))}
            </TableCell>
            <TableCell>
              {formatNumber(currentCountyPractice?.production, 0)}
              {" " + _getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase()}
            </TableCell>
            <TableCell>
              {formatNumber(currentCountyPractice?.production_guarantee)}
              {" " + _getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase()}
            </TableCell>
            <TableCell>
              {formatNumber(currentCountyPractice?.marketing_goal_units)}
              {" " + _getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase()}
            </TableCell>
            <TableCell>
              {formatNumber(currentCountyPractice?.unit_quantity_hedge_total)}
              {" " + _getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase()}
            </TableCell>
            <TableCell>
              {currentCountyPractice?.market_symbol}
              <br />
              {formatPrice(currentCountyPractice?.market_last_price, 4)}
              {_cropProfileBasis !== 0 && (
                <>
                  <br />
                  {`Basis: ${formatPrice(_cropProfileBasis, 4)}`}
                </>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
